import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../loader/Loader';
import { Animal } from '../../types/interfaces/animal.interfaces';
import { SpecimenInventory } from '../../types/interfaces/specimen.interfaces';
import { InventorySource, TransactionStatusEnum } from '../../types/enums';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { getOnFarmAnimalAttachmentURL } from '../../api/onFarmAnimalsApi';
import { getOnFarmSpecimensByOwnership } from '../../services/onFarmInventory.service';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { toastMessages } from '../../constants/errorMessages';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import { getAnimalAttachmentURL } from '../../services/animal.service';
import { filtersType, InventoryLocationState } from '../../pages/inventory/Inventory';
import { ViewMode } from '../../types/enums/viewMode.enum';
import { showToast } from '../../services/toast.service';

const AnimalCard: FC<{
  animal: Animal;
  filterPreset?: filtersType;
  pagingOffset?: number;
  scrollY?: number;
  viewType?: ViewMode;
  accountId?: number;
  inventorySource?: InventorySource;
}> = ({
  animal,
  filterPreset,
  pagingOffset,
  scrollY,
  viewType,
  accountId,
  inventorySource = InventorySource.RSG,
}): JSX.Element => {
  const navigate = useNavigate();

  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [unitsOwned, setUnitsOwned] = useState<number>();

  const onViewProfile = () => {
    if (inventorySource === InventorySource.ON_FARM) {
      navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animal.animalId);
      return;
    }

    const listState: InventoryLocationState = {
      filterPreset,
      scrollY,
      pagingOffset,
      viewType,
    };
    navigate(ROUTE_PATHS.APP_INVENTORY + '/' + animal.animalId, { state: listState });
  };

  const getUnits = async () => {
    if (animal.animalId && accountId) {
      try {
        if (inventorySource === InventorySource.ON_FARM) {
          const { data: specs } = await getOnFarmSpecimensByOwnership(animal.animalId, accountId);
          const quantityOwned = specs.reduce((total, specimen) => total + specimen.quantityOwned, 0);
          setUnitsOwned(quantityOwned);
          return;
        }

        const params = {
          excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
        };
        const specs = await getSpecimensByOwnership(animal.animalId, accountId, params);
        let quantityOwned = 0;
        specs.data.forEach((specimen: SpecimenInventory) => (quantityOwned += specimen.quantityOwned));
        setUnitsOwned(quantityOwned);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  };

  useEffect(() => {
    getUnits();
  }, [animal.animalId, accountId, inventorySource]);

  useEffect(() => {
    if (animal.attachments && animal.attachments.length > 0) {
      const attachment = animal.attachments[0];
      if (inventorySource === InventorySource.ON_FARM) {
        getOnFarmAnimalAttachmentURL(animal.animalId!, attachment.attachmentId, attachment.accessToken).then(setImageUrl);
        return;
      }

      setImageUrl(getAnimalAttachmentURL(animal.animalId!, attachment.attachmentId, attachment.accessToken));
    }
  }, [animal, inventorySource]);

  return (
    <div className="animal-card">
      {imageUrl ? (
        <div className="image-container">
          {imageLoading && (
            <div className="image-loader-container">
              <Loader loaderSize={'small'} />
            </div>
          )}
          <img
            src={imageUrl}
            alt="Animal Image"
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
            style={{ display: imageLoading ? 'none' : 'block' }}
          />
        </div>
      ) : (
        <div className="animal-image-placeholder">{ANIMAL_CONSTANTS.NO_IMAGE}</div>
      )}
      <div className="animal-info">
        <h3>{animal.name}</h3>
        <p className="animal-short-details">
          {ANIMAL_CONSTANTS.SIRE}: {animal.sire} | {ANIMAL_CONSTANTS.DAM}: ({animal.dam}) | {ANIMAL_CONSTANTS.DNA}: {animal.dna}
        </p>
        <p>
          <u>
            {LABEL_CONSTANTS.UNITS_OWNED}: {unitsOwned ?? animal?.quantityOwned ?? '--'}
          </u>
        </p>
        <button className="button green" type="button" onClick={onViewProfile}>
          {BUTTON_CONSTANTS.VIEW_PROFILE}
        </button>
      </div>
    </div>
  );
};

export default AnimalCard;
